import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles, getArticlesByCategory } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "career");

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = getArticlesByCategory(activeCategory);

  const webpageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${currentCategory?.name || "分类"} - 第一次指南`,
    description: currentCategory?.description || "",
    url: `https://nervousnix.github.io/category/${activeCategory}`,
    isPartOf: { "@type": "WebSite", name: "第一次指南", url: "https://nervousnix.github.io" },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: currentCategory?.name || "分类" },
              ]}
            />

            <div className="mt-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                {currentCategory?.name || "分类"}
              </h1>
              {currentCategory && (
                <p className="text-foreground-600 max-w-2xl leading-relaxed">
                  {currentCategory.description}
                </p>
              )}
            </div>

            <div className="mt-8 flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-200/70 text-foreground-600 hover:bg-background-300/60 hover:text-foreground-900"
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    slug={article.slug}
                    title={article.title}
                    category={article.category}
                    excerpt={article.excerpt}
                    author={article.author}
                    publishDate={article.publishDate}
                    readTime={article.readTime}
                    imageUrl={article.imageUrl}
                    categoryName={currentCategory?.name || article.category}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200/70">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}